import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';
import { admin } from './utils/api';
import socketManager from './utils/socketManager';

// Components
import { AuthProvider, useAuth } from './components/auth/AuthModule';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/shared/Layout';
import LoginComponent from './components/auth/LoginComponent';
import RegisterComponent from './components/auth/RegisterComponent';
import DashboardPage from './pages/dashboard/DashboardPage';
import WorkshopPage from './pages/workshop/WorkshopPage';
import TaskDetailPage from './pages/workshop/TaskDetailPage';
import EventsPage from './pages/events/EventsPage';
import EventDetailPage from './pages/events/EventDetailPage';
import LandingEventsPage from './pages/events/LandingEventsPage';
import CalendarPage from './pages/calendar/CalendarPage';
import TeamPage from './pages/team/TeamPage';
import CarsPage from './pages/cars/CarsPage';
import CarDetailPage from './pages/cars/CarDetailPage';
import CarReplacementDetailPage from './pages/cars/CarReplacementDetailPage';
import RacetracksPage from './pages/racetracks/RacetracksPage';
import RacetrackDetailPage from './pages/racetracks/RacetrackDetailPage';
import AdminPage from './pages/admin/AdminPage';
import GalleryPage from './pages/gallery/GalleryPage';
import GalleryDetailPage from './pages/gallery/GalleryDetailPage';
import PaymentsPage from './pages/payments/PaymentsPage';
import SponsorsPage from './pages/sponsors/SponsorsPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import NoticeboardPage from './pages/noticeboard/NoticeboardPage';
import CommsPage from './pages/communications/CommsPage';
import PageTitle from './components/shared/PageTitle';
import TestDragDrop from './pages/communications/components/TestDragDrop';

const THEME_STORAGE_KEY = 'motorteam_theme_settings';

const App = () => {
  const [themeSettings, setThemeSettings] = useState({
    primaryColor: '#cc3333',
    secondaryColor: '#cc3333',
    backgroundColor: '#000000',
    paperColor: '#121212'
  });

  useEffect(() => {
    const fetchThemeSettings = async () => {
      try {
        // First try to get from localStorage for immediate display
        const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        if (storedTheme) {
          setThemeSettings(JSON.parse(storedTheme));
        }

        // Then fetch from server and update if different
        const settings = await admin.getPublicSiteSettings();
        if (settings?.theme) {
          setThemeSettings(settings.theme);
          localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(settings.theme));
        }
      } catch (error) {
        console.error('Error fetching theme settings:', error);
      }
    };

    fetchThemeSettings();

    // Listen for theme updates from other components
    const handleThemeUpdate = (event) => {
      if (event.key === THEME_STORAGE_KEY) {
        try {
          const newTheme = JSON.parse(event.newValue);
          if (newTheme) {
            setThemeSettings(newTheme);
          }
        } catch (error) {
          console.error('Error parsing theme settings:', error);
        }
      }
    };

    window.addEventListener('storage', handleThemeUpdate);
    return () => window.removeEventListener('storage', handleThemeUpdate);
  }, []);

  // Initialize socket connection
  useEffect(() => {
    socketManager.initSocket();

    // Clean up socket connection on unmount
    return () => {
      socketManager.disconnectSocket();
    };
  }, []);

  const theme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: themeSettings.primaryColor,
      },
      secondary: {
        main: themeSettings.secondaryColor,
      },
      background: {
        default: themeSettings.backgroundColor,
        paper: themeSettings.paperColor,
      },
    },
    components: {
      MuiLink: {
        styleOverrides: {
          root: {
            color: '#ffffff',
            '&:hover': {
              color: '#ffffff',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: "#6b6b6b #2b2b2b",
            "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
              backgroundColor: themeSettings.paperColor,
            },
            "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
              borderRadius: 8,
              backgroundColor: themeSettings.primaryColor,
              minHeight: 24,
              border: `3px solid ${themeSettings.paperColor}`,
            },
            "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
              backgroundColor: themeSettings.secondaryColor,
            },
            "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
              backgroundColor: themeSettings.secondaryColor,
            },
            "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
              backgroundColor: themeSettings.secondaryColor,
            },
            "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
              backgroundColor: themeSettings.paperColor,
            },
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <PageTitle />
        <AuthProvider>
          <Routes>
            {/* Routes without the standard Layout */}
            <Route path="/events-landing" element={<LandingEventsPage />} />
            <Route path="/" element={<LandingEventsPage />} />
            <Route path="/login" element={<LoginComponent />} />
            <Route path="/register" element={<RegisterComponent />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            
            {/* Routes with the standard Layout */}
            <Route element={<Layout />}>
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workshop"
                element={
                  <ProtectedRoute>
                    <WorkshopPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workshop/tasks/:taskId"
                element={
                  <ProtectedRoute>
                    <TaskDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events"
                element={
                  <ProtectedRoute>
                    <EventsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/:eventId"
                element={
                  <ProtectedRoute>
                    <EventDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <CalendarPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cars"
                element={
                  <ProtectedRoute>
                    <CarsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cars/:id"
                element={
                  <ProtectedRoute>
                    <CarDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cars/:carId/replacements/:replacementId"
                element={
                  <ProtectedRoute>
                    <CarReplacementDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/racetracks"
                element={
                  <ProtectedRoute>
                    <RacetracksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/racetracks/:id"
                element={
                  <ProtectedRoute>
                    <RacetrackDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/galleries"
                element={
                  <ProtectedRoute>
                    <GalleryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/galleries/:id"
                element={
                  <ProtectedRoute>
                    <GalleryDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <PaymentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/communications"
                element={
                  <ProtectedRoute>
                    <CommsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/noticeboard"
                element={
                  <ProtectedRoute>
                    <NoticeboardPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Test routes */}
            <Route path="/test-dnd" element={<TestDragDrop />} />

            {/* Fallback route */}
          </Routes>
          <ToastContainer position="bottom-right" theme="dark" />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
